<template>
  <span
    ref="input"
    role="spinbutton"
    :contenteditable="!disabled"
    :data-segment="segment.type"
    :data-placeholder="segment.value === null"
    :tabindex="disabled ? -1 : 0"
    :aria-label="segment.type"
    :aria-valuenow="segment.value ?? undefined"
    :aria-valuetext="ariaValueText"
    :aria-valuemin="segment.min"
    :aria-valuemax="segment.max"
    :style="{ caretColor: 'transparent' }"
    inputmode="numeric"
    autocorrect="off"
    spellcheck="false"
    enterkeyhint="next"
    class="select-none rounded px-1 tabular-nums outline-none focus:bg-blue-500 focus:text-white"
    @focus="handleFocus"
    @blur="handleBlur"
    @keydown="handleKeydown"
    @beforeinput="handleBeforeInput"
    >{{ displayValue }}</span
  >
</template>

<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";

import { formatMonthName } from "@/lib/time";

export interface SegmentData {
  type: "day" | "month" | "year";
  value: number | null;
  min: number;
  max: number;
}

const props = defineProps<{
  segment: SegmentData;
  disabled?: boolean;
}>();

const emit = defineEmits<{
  change: [value: number | null];
  increment: [delta: number];
  next: [];
  prev: [];
  clear: [];
}>();

const input = useTemplateRef("input");
const buffer = ref("");

const displayValue = computed(() => {
  const { type, value } = props.segment;

  if (value === null) {
    switch (type) {
      case "day": {
        return "dd";
      }
      case "month": {
        return "mm";
      }
      case "year": {
        return "yyyy";
      }
    }
  }

  switch (type) {
    case "day": {
      return value.toString().padStart(2, "0");
    }
    case "month": {
      return value.toString().padStart(2, "0");
    }
    case "year": {
      return value.toString().padStart(4, "0");
    }
  }

  return "";
});

const ariaValueText = computed(() => {
  const { type, value } = props.segment;
  if (value === null) {
    return undefined;
  }

  if (type === "month") {
    return formatMonthName(value);
  }

  return value.toString();
});

const focus = (): void => {
  input.value?.focus();
};

const handleFocus = (): void => {
  buffer.value = "";

  // Collapse selection to hide cursor
  const selection = window.getSelection();
  if (selection && input.value) {
    selection.collapse(input.value);
  }
};

const handleBlur = (): void => {
  buffer.value = "";
};

const handleKeydown = (event: KeyboardEvent): void => {
  if (props.disabled) {
    return;
  }

  switch (event.key) {
    case "ArrowLeft": {
      event.preventDefault();
      emit("prev");

      buffer.value = "";
      break;
    }
    case "ArrowRight": {
      event.preventDefault();
      emit("next");

      buffer.value = "";
      break;
    }
    case "ArrowUp": {
      event.preventDefault();
      emit("increment", 1);

      buffer.value = "";
      break;
    }
    case "ArrowDown": {
      event.preventDefault();
      emit("increment", -1);

      buffer.value = "";
      break;
    }
    case "Backspace":
    case "Delete": {
      event.preventDefault();
      handleBackspace();
      break;
    }
  }
};

const handleBeforeInput = (event: Event): void => {
  event.preventDefault();

  if (props.disabled) {
    return;
  }

  const ev = event as InputEvent;
  const data = ev.data;
  switch (data) {
    case "0":
    case "1":
    case "2":
    case "3":
    case "4":
    case "5":
    case "6":
    case "7":
    case "8":
    case "9": {
      handleDigitInput(data);
      break;
    }
  }
};

const handleDigitInput = (digit: string): void => {
  const { type, max } = props.segment;

  const newBuffer = buffer.value + digit;
  let num = parseInt(newBuffer, 10);

  if (type === "year") {
    // Year accumulates up to 4 digits, but doesn't auto-advance
    buffer.value = newBuffer.slice(0, 4);
    emit("change", parseInt(buffer.value, 10));

    if (buffer.value.length >= 4) {
      buffer.value = "";
    }
  } else {
    // Day and month have overflow behavior
    if (num > max) {
      num = parseInt(digit, 10);
      buffer.value = digit;
    } else {
      buffer.value = newBuffer;
    }

    emit("change", num);

    // Auto-advance when we can't add more digits
    if (num * 10 > max || buffer.value.length >= 2) {
      buffer.value = "";
      emit("next");
    }
  }
};

const handleBackspace = (): void => {
  const { value } = props.segment;

  // If segment is empty, navigate to previous
  if (value === null) {
    emit("prev");
    return;
  }

  // Remove last digit
  const displayedStr = value.toString();
  const newStr = displayedStr.slice(0, -1);

  if (newStr.length === 0) {
    emit("change", null);
    buffer.value = "";
  } else {
    const num = parseInt(newStr, 10);
    emit("change", num);
    buffer.value = newStr;
  }
};

defineExpose({
  focus,
  el: input,
});
</script>
